import React, { useState, useEffect } from "react";
import { 
  HiPencil, HiCheckCircle, HiPlus, HiSearch, 
  HiOutlineUserGroup, HiOutlinePencilAlt, HiShieldExclamation, HiClock
} from "react-icons/hi";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import * as bootstrap from "bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { PulseLoader } from "react-spinners";
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

const statusOptions = [
  { value: "Not Started", label: "Not Started", color: "#6c757d" },
  { value: "In Progress", label: "In Progress", color: "#0d6efd" },
  { value: "On Hold", label: "On Hold", color: "#ffc107" },
  { value: "Completed", label: "Completed", color: "#198754" },
  { value: "Cancelled", label: "Cancelled", color: "#dc3545" }
];

const DeveloperProjectManagement = () => {
  // State management
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(5);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rolemanagement, setRoleManagement] = useState("");

  // Form states
  const [projectId, setProjectId] = useState(null);
  const [name, setName] = useState("");
  const [customer, setCustomer] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("Not Started");
  const [teamMembers, setTeamMembers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [developerId, setDeveloperId] = useState("");
  const [isFetchingDropdowns, setIsFetchingDropdowns] = useState(false);

  // Authorization header
  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token")}`
    }
  });

  // Initialize component
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setRoleManagement(payload.role);
        const userId = payload.id;
        setDeveloperId(userId);
        console.log("Developer ID from token:", userId);
        
        // Fetch initial data
        fetchProjects(userId);
        fetchCustomersAndDevelopers();
      } catch (err) {
        console.error("Invalid token", err);
        toast.error("Failed to authenticate user");
      }
    }
  }, []);

  const fetchProjects = async (devId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `http://localhost:5890/getprojectsfiler?developerId=${devId}`,
        getAuthHeader()
      );
      setProjects(response.data);
      console.log("Projects data:", response.data);
    } catch (err) {
      console.error("Error fetching projects:", err);
      toast.error("❌ Failed to fetch projects");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch dropdown data
  const fetchCustomersAndDevelopers = async () => {
    setIsFetchingDropdowns(true);
    try {
      // Fetch customers
      const customersResponse = await axios.get(
        "http://localhost:5890/GETMENSTIONAPI/Bussiness_all_details",
        getAuthHeader()
      );
      
      const customerOptions = customersResponse.data.customers.map(c => ({
        value: c._id,
        label: c.lastName,
        email: c.email,
        company: c.company
      }));
      setCustomers(customerOptions);
      
      // Fetch developers
      const developersResponse = await axios.get(
        "http://localhost:5890/adminperson/developers",
        getAuthHeader()
      );
      
      const developerOptions = developersResponse.data.map(d => ({
        value: d._id,
        label: d.developerName,
        skills: d.skills,
        avatar: d.avatar
      }));
      setDevelopers(developerOptions);
      
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    } finally {
      setIsFetchingDropdowns(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setProjectId(null);
    setName("");
    setCustomer("");
    setStartDate("");
    setEndDate("");
    setStatus("Not Started");
    setTeamMembers([]);
    setIsEditing(false);
  };

  // Handle add or edit project
  const handleAddOrEditProject = async () => {
    if (!name) {
      toast.warn("⚠️ Project name is required");
      return;
    }

    const projectData = isEditing 
      ? { status } // Only send status when editing
      : {
          name,
          customer: customer.value || customer,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          status,
          teamMembers: teamMembers.map(member => ({ developer: member.value }))
        };

    try {
      if (isEditing) {
        await axios.put(
          `http://localhost:5890/editproject/${projectId}`,
          projectData,
          getAuthHeader()
        );
        toast.success("🎉 Project status updated successfully!");
      } else {
        await axios.post(
          "http://localhost:5890/addproject",
          projectData,
          getAuthHeader()
        );
        toast.success("🎉 Project added successfully!");
      }
      
      document.getElementById("closeModalBtn").click();
      resetForm();
      fetchProjects(developerId);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.error || error.message || "Operation failed");
    }
  };

  const handleEditProject = (project) => {
    setIsEditing(true);
    setProjectId(project._id);
    setName(project.name);
    setStatus(project.status || "Not Started");

    const modal = new bootstrap.Modal(document.getElementById("addProjectModal"));
    modal.show();
  };

  // Delete project handlers
  const confirmDeleteProject = (project) => {
    setProjectToDelete(project);
    const modal = new bootstrap.Modal(document.getElementById("deleteConfirmationModal"));
    modal.show();
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(
        `http://localhost:5890/deleteproject/${projectToDelete._id}`,
        getAuthHeader()
      );
      toast.success("🗑️ Project deleted successfully");
      fetchProjects(developerId);
      document.getElementById("closeDeleteModalBtn").click();
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error("Failed to delete project");
    }
  };

  // Filter and pagination logic
  const filteredProjects = projects.filter((project) => {
    const searchTerm = search.toLowerCase();
    const customerObj = customers.find(c => c.value === project.customer);
    const customerName = customerObj ? customerObj.label.toLowerCase() : "";
    
    return (
      project.name?.toLowerCase().includes(searchTerm) ||
      customerName.includes(searchTerm) ||
      project.status?.toLowerCase().includes(searchTerm)
    );
  });

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Custom select styles
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      border: "1px solid #dee2e6",
      borderRadius: "0.375rem",
      minHeight: "42px",
      boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#f8f9fa" : "white",
      color: state.isSelected ? "white" : "#212529",
      display: "flex",
      alignItems: "center",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e9ecef",
      borderRadius: "4px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontWeight: "500",
    }),
  };

  // Helper functions
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : "#6c757d";
  };

  const getCustomerName = (customerId) => {
    const customerObj = customers.find(c => c.value === customerId);
    return customerObj ? customerObj.label : customerId;
  };

  // Custom components for Select
  const DeveloperOption = ({ innerProps, label, data }) => (
    <div {...innerProps} className="px-3 py-2 d-flex align-items-center">
      {data.avatar && (
        <img 
          src={data.avatar} 
          alt={label} 
          className="rounded-circle me-3" 
          width="32" 
          height="32"
        />
      )}
      <div>
        <div className="fw-semibold">{label}</div>
        {data.skills && (
          <small className="text-muted">
            {data.skills.join(', ')}
          </small>
        )}
      </div>
    </div>
  );

  const MultiValue = ({ data, removeProps }) => (
    <div className="d-flex align-items-center bg-light rounded px-2 py-1 me-2">
      {data.avatar && (
        <img 
          src={data.avatar} 
          alt={data.label} 
          className="rounded-circle me-2" 
          width="24" 
          height="24"
        />
      )}
      <span className="me-2">{data.label}</span>
      <button 
        {...removeProps} 
        className="btn btn-sm p-0 text-danger"
        style={{ lineHeight: 1 }}
      >
        &times;
      </button>
    </div>
  );
  console.log("roleManagement",rolemanagement);
  

  return (
    <div className="container-fluid px-4 py-3">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      
      {/* Main Card */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        {/* Card Header */}
        <div className="card-header bg-white border-0 py-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <h5 className="mb-2 mb-md-0 fw-semibold d-flex align-items-center gap-2">
              <HiOutlineUserGroup size={35} className="text-primary" />
              Project Management
            </h5>     
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
              <div className="position-relative flex-grow-1 col-7" style={{ maxWidth: "400px" }}>
                <HiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                <input
                  type="text"
                  className="form-control ps-5 py-2 rounded-pill shadow-sm border-0"
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ backgroundColor: "#f8f9fa" }}
                />
              </div>
              
           
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100 rounded-3 bg-primary bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted fw-semibold small">Total Projects</h6>
                    <h3 className="mb-0 fw-bold">{projects.length}</h3>
                  </div>
                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                    <HiOutlineUserGroup size={24} className="text-primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100 rounded-3 bg-success bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted fw-semibold small">Completed</h6>
                    <h3 className="mb-0 fw-bold">{projects.filter(p => p.status === "Completed").length}</h3>
                  </div>
                  <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                    <HiCheckCircle size={24} className="text-success" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100 rounded-3 bg-warning bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted fw-semibold small">In Progress</h6>
                    <h3 className="mb-0 fw-bold">{projects.filter(p => p.status === "In Progress").length}</h3>
                  </div>
                  <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                    <HiClock size={24} className="text-warning" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100 rounded-3 bg-info bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted fw-semibold small">On Hold</h6>
                    <h3 className="mb-0 fw-bold">{projects.filter(p => p.status === "On Hold").length}</h3>
                  </div>
                  <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                    <HiShieldExclamation size={24} className="text-info" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Project Name</th>
                  <th>Customer</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Team Size</th>
                  {["Developer","Designer"].includes(rolemanagement) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <PulseLoader color="#0d6efd" size={10} />
                    </td>
                  </tr>
                ) : currentProjects.length > 0 ? (
                  currentProjects.map((project) => (
                    <tr key={project._id} className="border-top">
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-primary bg-opacity-10 p-2 rounded-2">
                            <HiOutlineUserGroup size={24} className="text-primary" />
                          </div>
                          <div>
                            <span className="fw-semibold d-block">{project.name}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-medium">{getCustomerName(project.customer)}</span>
                          {customers.find(c => c.value === project.customer)?.company && (
                            <small className="text-muted">
                              {customers.find(c => c.value === project.customer)?.company}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        {project.startDate ? new Date(project.startDate).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        {project.endDate ? new Date(project.endDate).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill fw-semibold px-3 py-1`}
                          style={{ 
                            backgroundColor: `${getStatusColor(project.status)}20`,
                            color: getStatusColor(project.status),
                            width: 'fit-content'
                          }}
                        >
                          {project.status}
                        </span>
                      </td>
                      <td>
                        {project.teamMembers?.length || 0}
                      </td>
                      {["Developer","Designer"].includes(rolemanagement) && (
                        <td>
                          <div className="d-flex">
                            <button
                              className="btn btn-sm btn-outline-primary rounded-pill px-3"
                              onClick={() => handleEditProject(project)}
                              title="Update Status"
                            >
                              <HiOutlinePencilAlt size={16} />
                            </button>
                           
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      <div className="py-4">
                        <img src="/empty-state.svg" alt="No projects" width="120" className="mb-3 opacity-75" />
                        <h5 className="fw-semibold">No projects found</h5>
                        <p className="text-muted">Try adjusting your search or add a new project</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination */}
        {filteredProjects.length > 0 && (
          <div className="card-footer bg-white border-0 py-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
              <div className="mb-2 mb-md-0 small text-muted">
                Showing {indexOfFirstProject + 1} to {Math.min(indexOfLastProject, filteredProjects.length)} of {filteredProjects.length} entries
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center">
                  <label htmlFor="perPageSelect" className="form-label mb-0 me-2 small">Show:</label>
                  <select
                    id="perPageSelect"
                    className="form-select form-select-sm"
                    style={{ width: '70px' }}
                    value={projectsPerPage}
                    onChange={(e) => {
                      setProjectsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <nav aria-label="Page navigation">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                    </li>
                    {[...Array(totalPages).keys()].map(number => (
                      <li key={number + 1} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(number + 1)}>
                          {number + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

    <div className="modal fade" id="addProjectModal" tabIndex="-1" aria-labelledby="addProjectModalLabel" aria-hidden="true">
  <div className="modal-dialog modal-lg modal-dialog-centered">
    <div className="modal-content shadow-lg border-0 rounded-3">
      <div className="modal-header bg-primary text-white rounded-top-3">
        <h5 className="modal-title" id="addProjectModalLabel">
          {isEditing ? (
            <>
              <HiPencil className="me-2" />
              Update Project Status
            </>
          ) : (
            <>
              <HiPlus className="me-2" />
              Add New Project
            </>
          )}
        </h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          data-bs-dismiss="modal"
          aria-label="Close"
          id="closeModalBtn"
        ></button>
      </div>

      <div className="modal-body px-4 py-3">
        {isFetchingDropdowns ? (
          <div className="text-center py-4">
            <PulseLoader color="#0d6efd" size={10} />
            <p className="mt-2">Loading form data...</p>
          </div>
        ) : (
          <div className="row g-3">
            {isEditing ? (
              // Enhanced Edit Mode - Status Only
              <>
                <div className="col-12">
                  <div className="card border-0 bg-light p-3 mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-2">
                        <HiOutlineUserGroup size={24} className="text-primary" />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">{name}</h6>
                        <small className="text-muted">
                          Current Status: 
                          <span 
                            className="badge rounded-pill ms-2"
                            style={{ 
                              backgroundColor: `${getStatusColor(status)}20`,
                              color: getStatusColor(status)
                            }}
                          >
                            {status}
                          </span>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="fw-semibold mb-3 text-primary">
                        <HiOutlinePencilAlt className="me-2" />
                        Update Project Status
                      </h6>
                      
                      <div className="row">
                        <div className="col-md-8">
                          <label className="form-label fw-semibold">
                            New Status <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={statusOptions}
                            value={statusOptions.find(option => option.value === status)}
                            onChange={(selected) => setStatus(selected.value)}
                            styles={customSelectStyles}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Select new status..."
                            formatOptionLabel={(option) => (
                              <div className="d-flex align-items-center">
                                <span 
                                  className="rounded-circle me-2" 
                                  style={{
                                    width: "10px", 
                                    height: "10px", 
                                    backgroundColor: option.color
                                  }}
                                ></span>
                                <span>{option.label}</span>
                                {option.value === status && (
                                  <span className="ms-auto badge bg-success bg-opacity-10 text-success">
                                    Current
                                  </span>
                                )}
                              </div>
                            )}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">
                            Last Updated
                          </label>
                          <div className="form-control bg-light">
                            {new Date().toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="form-label fw-semibold">
                          Status Notes (Optional)
                        </label>
                        <textarea
                          className="form-control"
                          rows="2"
                          placeholder="Add any notes about this status change..."
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Original Add Mode
              <>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Project Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter project name"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Customer
                  </label>
                  <Select
                    options={customers}
                    value={customers.find(option => option.value === (customer.value || customer))}
                    onChange={(selected) => setCustomer(selected)}
                    styles={customSelectStyles}
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select customer..."
                    isClearable
                    isLoading={isFetchingDropdowns}
                    loadingMessage={() => "Loading customers..."}
                    noOptionsMessage={() => "No customers found"}
                  />
                  {customer && customer.email && (
                    <small className="text-muted mt-1 d-block">
                      {customer.email}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Status <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find(option => option.value === status)}
                    onChange={(selected) => setStatus(selected.value)}
                    styles={customSelectStyles}
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select status..."
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center">
                        <span 
                          className="rounded-circle me-2" 
                          style={{
                            width: "10px", 
                            height: "10px", 
                            backgroundColor: option.color
                          }}
                        ></span>
                        {option.label}
                      </div>
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Team Members
                  </label>
                  <Select
                    options={developers}
                    value={teamMembers}
                    onChange={(selected) => setTeamMembers(selected || [])}
                    styles={customSelectStyles}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select team members..."
                    isMulti
                    isClearable
                    isLoading={isFetchingDropdowns}
                    loadingMessage={() => "Loading developers..."}
                    noOptionsMessage={() => "No developers found"}
                    components={{
                      Option: DeveloperOption,
                      MultiValue,
                      ...animatedComponents
                    }}
                    closeMenuOnSelect={false}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="modal-footer border-0 rounded-bottom-3 px-4 py-3">
        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          data-bs-dismiss="modal"
          disabled={isFetchingDropdowns}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
          onClick={handleAddOrEditProject}
          disabled={isFetchingDropdowns}
        >
          {isEditing ? (
            <>
              <HiCheckCircle size={16} />
              Confirm Update
            </>
          ) : (
            <>
              <HiPlus size={16} />
              Add Project
            </>
          )}
        </button>
      </div>
    </div>
  </div>
</div>
    </div>
  );
};

export default DeveloperProjectManagement;